// import React from 'react';

// const OptionsWidget = (props) => {
//   return (
//     <div><pre>{ JSON.stringify(props.value, null, 2) }</pre></div>
//   );
// };

// export default OptionsWidget;
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Card, Label, List } from 'semantic-ui-react';

const messages = defineMessages({
  unnamedGroup: {
    id: 'optionsWidget.unnamedGroup',
    defaultMessage: 'Option group {index}',
  },
  substitutesBasePrice: {
    id: 'optionsWidget.substitutesBasePrice',
    defaultMessage: 'Replaces base price',
  },
  selectionRules: {
    id: 'optionsWidget.selectionRules',
    defaultMessage: 'Selection rules',
  },
  minDifferentOptions: {
    id: 'optionsWidget.minDifferentOptions',
    defaultMessage: 'Min. different options',
  },
  maxDifferentOptions: {
    id: 'optionsWidget.maxDifferentOptions',
    defaultMessage: 'Max. different options',
  },
  minTotalQuantity: {
    id: 'optionsWidget.minTotalQuantity',
    defaultMessage: 'Min. total quantity',
  },
  maxTotalQuantity: {
    id: 'optionsWidget.maxTotalQuantity',
    defaultMessage: 'Max. total quantity',
  },
  noMinimum: {
    id: 'optionsWidget.noMinimum',
    defaultMessage: 'No minimum',
  },
  unlimited: {
    id: 'optionsWidget.unlimited',
    defaultMessage: 'Unlimited',
  },
  optionsTitle: {
    id: 'optionsWidget.optionsTitle',
    defaultMessage: 'Options',
  },
  optionDefault: {
    id: 'optionsWidget.optionDefault',
    defaultMessage: 'Default',
  },
  optionPrice: {
    id: 'optionsWidget.optionPrice',
    defaultMessage: 'Price: {value}',
  },
  optionMaxQuantity: {
    id: 'optionsWidget.optionMaxQuantity',
    defaultMessage: 'Max. quantity: {value}',
  },
  noOptions: {
    id: 'optionsWidget.noOptions',
    defaultMessage: 'No options have been configured for this group.',
  },
  unnamedOption: {
    id: 'optionsWidget.unnamedOption',
    defaultMessage: 'Option {index}',
  },
});

const OptionsWidget = ({ value }) => {
  const intl = useIntl();

  const groups = React.useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (Array.isArray(value.items)) return value.items;
    return [];
  }, [value]);

  const formatMinimum = React.useCallback(
    (val) =>
      typeof val === 'number' && val > 0
        ? intl.formatNumber(val)
        : intl.formatMessage(messages.noMinimum),
    [intl],
  );

  const formatMaximum = React.useCallback(
    (val) =>
      typeof val === 'number' && val > 0
        ? intl.formatNumber(val)
        : intl.formatMessage(messages.unlimited),
    [intl],
  );

  const groupCardStyle = React.useMemo(
    () => ({
      borderRadius: '12px',
      border: '1px solid #dadee6',
      boxShadow: 'none',
    }),
    [],
  );

  const headerRowStyle = React.useMemo(
    () => ({
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '0.75rem',
      marginBottom: '0.75rem',
      flexWrap: 'wrap',
    }),
    [],
  );

  const rulesWrapperStyle = React.useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      gap: '0.35rem',
      marginBottom: '1rem',
    }),
    [],
  );

  const ruleBadgesStyle = React.useMemo(
    () => ({
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.4rem',
    }),
    [],
  );

  const optionHeaderStyle = React.useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '0.5rem',
    }),
    [],
  );

  const optionMetaStyle = React.useMemo(
    () => ({
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.4rem',
      marginTop: '0.5rem',
    }),
    [],
  );

  if (groups.length === 0) {
    return null;
  }

  return (
    <Card.Group
      stackable
      doubling
      itemsPerRow={2}
      className="options-view-widget"
      role="list"
    >
      {groups.map((group, index) => {
        const {
          ['@id']: id,
          name,
          description,
          minDifferentOptions,
          maxDifferentOptions,
          minTotalQuantity,
          maxTotalQuantity,
          substitutesBasePrice,
          options,
        } = group || {};

        const key = id || `${name || 'group'}-${index}`;

        const optionItems = Array.isArray(options?.items) ? options.items : [];
        const hasOptionDescription = optionItems.some(
          (item) => item?.description,
        );

        return (
          <Card key={key} role="listitem" style={groupCardStyle}>
            <Card.Content>
              <div style={headerRowStyle}>
                <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                  <Card.Header
                    as="h3"
                    content={
                      name ||
                      intl.formatMessage(messages.unnamedGroup, {
                        index: index + 1,
                      })
                    }
                  />
                  {description && (
                    <Card.Meta
                      style={{ marginTop: '0.35rem', color: '#4a4f5c' }}
                    >
                      {description}
                    </Card.Meta>
                  )}
                </div>
                {substitutesBasePrice && (
                  <Label color="orange" size="small">
                    {intl.formatMessage(messages.substitutesBasePrice)}
                  </Label>
                )}
              </div>

              <div style={rulesWrapperStyle}>
                <strong style={{ color: '#4a4f5c', fontSize: '0.9rem' }}>
                  {intl.formatMessage(messages.selectionRules)}
                </strong>
                <div style={ruleBadgesStyle}>
                  <Label size="tiny" basic color="blue">
                    {intl.formatMessage(messages.minDifferentOptions)}:{' '}
                    {formatMinimum(minDifferentOptions)}
                  </Label>
                  <Label size="tiny" basic color="blue">
                    {intl.formatMessage(messages.maxDifferentOptions)}:{' '}
                    {formatMaximum(maxDifferentOptions)}
                  </Label>
                  <Label size="tiny" basic color="teal">
                    {intl.formatMessage(messages.minTotalQuantity)}:{' '}
                    {formatMinimum(minTotalQuantity)}
                  </Label>
                  <Label size="tiny" basic color="teal">
                    {intl.formatMessage(messages.maxTotalQuantity)}:{' '}
                    {formatMaximum(maxTotalQuantity)}
                  </Label>
                </div>
              </div>

              <div>
                <strong style={{ color: '#4a4f5c', fontSize: '0.9rem' }}>
                  {intl.formatMessage(messages.optionsTitle)}
                </strong>
                {optionItems.length > 0 ? (
                  <List divided relaxed style={{ marginTop: '0.5rem' }}>
                    {optionItems.map((option, idx) => {
                      const optionKey =
                        option?.['@id'] || `${option?.name || 'option'}-${idx}`;
                      const {
                        name: optionName,
                        description: optionDescription,
                      } = option || {};
                      const resolvedOptionName =
                        optionName ||
                        intl.formatMessage(messages.unnamedOption, {
                          index: idx + 1,
                        });
                      const hasPrice =
                        typeof option?.price === 'number' &&
                        !Number.isNaN(option.price);
                      const hasMaxQuantity =
                        typeof option?.maxQuantity === 'number' &&
                        !Number.isNaN(option.maxQuantity);

                      const priceValue = hasPrice
                        ? intl.formatNumber(option.price, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : null;

                      const maxQuantityValue = hasMaxQuantity
                        ? option.maxQuantity > 0
                          ? intl.formatNumber(option.maxQuantity)
                          : intl.formatMessage(messages.unlimited)
                        : null;

                      return (
                        <List.Item key={optionKey}>
                          <List.Content>
                            <div style={optionHeaderStyle}>
                              <List.Header
                                as="h4"
                                content={resolvedOptionName}
                              />
                              {option?.defaultOption && (
                                <Label color="green" size="mini" basic>
                                  {intl.formatMessage(messages.optionDefault)}
                                </Label>
                              )}
                            </div>
                            {optionDescription && (
                              <List.Description
                                style={{
                                  marginTop: '0.4rem',
                                  color: '#4a4f5c',
                                  lineHeight: 1.4,
                                }}
                                content={optionDescription}
                              />
                            )}
                            {(hasPrice || hasMaxQuantity) && (
                              <div
                                style={{
                                  ...optionMetaStyle,
                                  marginTop:
                                    optionDescription || hasOptionDescription
                                      ? '0.75rem'
                                      : '0.5rem',
                                }}
                              >
                                {hasPrice && (
                                  <Label size="mini" basic color="grey">
                                    {intl.formatMessage(messages.optionPrice, {
                                      value: priceValue,
                                    })}
                                  </Label>
                                )}
                                {hasMaxQuantity && (
                                  <Label size="mini" basic color="grey">
                                    {intl.formatMessage(
                                      messages.optionMaxQuantity,
                                      {
                                        value: maxQuantityValue,
                                      },
                                    )}
                                  </Label>
                                )}
                              </div>
                            )}
                          </List.Content>
                        </List.Item>
                      );
                    })}
                  </List>
                ) : (
                  <div style={{ marginTop: '0.5rem', color: '#6a7182' }}>
                    {intl.formatMessage(messages.noOptions)}
                  </div>
                )}
              </div>
            </Card.Content>
          </Card>
        );
      })}
    </Card.Group>
  );
};

export default OptionsWidget;
